import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { ApplicationFormState, NewApplicationForm, ApplicationFormAction } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError, ErrorType } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import dayjs from 'dayjs';

export const actions: ActionTree<ApplicationFormState, RootState> = {
    /**
     * 新規作成・更新前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままのエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit
     * @returns {Promise<void>}
     */
    async clearError({ commit }) {
        commit(types.CLEAR_APPLICATION_FORM_ERROR);
    },

    /**
     * 申請書一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            start_date: searchItems.startDate,
            end_date: searchItems.endDate,
            status: searchItems.status,
            word: searchItems.word,
            application_form_number: searchItems.application_form_number,
            only_my_tasks: searchItems.only_my_tasks,
            work_start_start_date: searchItems.work_start_start_date,
            work_start_end_date: searchItems.work_start_end_date,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.INDEX,
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SEARCH_FAILURE, params);
        }
    },

    /**
     * 申請書新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewApplicationForm} newApplicationForm 新規業務システム情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newApplicationForm: NewApplicationForm) {
        const drafted = newApplicationForm.base.drafted;
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.ApplicationForm.CREATE,
                    params: {
                        application_form: newApplicationForm,
                    },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                if (!drafted) {
                    const message = translator(i18n, MessageKey.ApplicationForm.CREATED);
                    dispatch('toastMessage', `${message}`, {
                        root: true,
                    });
                } else {
                    const message = translator(i18n, MessageKey.ApplicationForm.DRAFTED_CREATED);
                    dispatch('toastMessage', `${message}`, {
                        root: true,
                    });
                }
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * 申請書操作
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {ApplicationFormAction} applicationFormAction 申請書操作情報
     * @returns {Promise<void>}
     */
    async action({ dispatch, commit }, { applicationFormAction, successMessage }) {
        try {
            commit(types.ACTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.ApplicationForm.ACTION,
                    params: applicationFormAction,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ACTION_SUCCESS);

                // 成功トースト表示
                dispatch('toastMessage', `${successMessage}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ACTION_FAILURE, params);
        }
    },

    /**
     * 申請書詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 申請書ID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const showResData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            const rolesResData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.USER_ROLES.replace(':id', id.toString()),
                },
                { root: true }
            );
            const templateCheckForConfirmer = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.TEMPLATE_CHECK_FOR_CONFIRMER.replace(':id', id.toString()),
                },
                { root: true }
            );

            if (
                isHappenedOtherPartyError(showResData) ||
                isHappenedOtherPartyError(rolesResData) ||
                isHappenedOtherPartyError(templateCheckForConfirmer)
            ) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                const res = {
                    show: showResData,
                    roles: rolesResData,
                    templateCheckForConfirmer: templateCheckForConfirmer,
                };
                commit(types.SHOW_SUCCESS, res);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 申請書詳細情報添付ファイル取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 申請書ID
     * @returns {Promise<void>}
     */
    async files({ dispatch, commit }, id: number) {
        try {
            commit(types.FILES_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.FILES.replace(':id', id.toString()),
                },
                { root: true }
            );

            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.FILES_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.FILES_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 申請書詳細情報検知ルール取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 申請書ID
     * @returns {Promise<void>}
     */
    async detectionRules({ dispatch, commit }, id: number) {
        try {
            commit(types.DETECTION_RULES_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.DETECTION_RULES.replace(':id', id.toString()),
                },
                { root: true }
            );

            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DETECTION_RULES_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.DETECTION_RULES_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 申請書詳細情報検知メール取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 申請書ID
     * @returns {Promise<void>}
     */
    async mailDestinations({ dispatch, commit }, id: number) {
        try {
            commit(types.MAIL_DESTINATIONS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.MAIL_DESTINATIONS.replace(':id', id.toString()),
                },
                { root: true }
            );

            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.MAIL_DESTINATIONS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.MAIL_DESTINATIONS_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 申請書更新作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewApplicationForm} newApplicationForm 更新業務システム情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, newApplicationForm: NewApplicationForm) {
        const drafted = newApplicationForm.base.drafted;
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.ApplicationForm.UPDATE.replace(':id', newApplicationForm.base.id.toString()),
                    params: { application_form: newApplicationForm },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                if (!drafted) {
                    const message = translator(i18n, MessageKey.ApplicationForm.UPDATED);
                    dispatch('toastMessage', `${message}`, {
                        root: true,
                    });
                } else {
                    const message = translator(i18n, MessageKey.ApplicationForm.DRAFTED_UPDATED);
                    dispatch('toastMessage', `${message}`, {
                        root: true,
                    });
                }
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { searchItems = {}, sort } = {}) {
        const payload = {
            start_date: searchItems.startDate,
            end_date: searchItems.endDate,
            status: searchItems.status,
            word: searchItems.word,
            application_form_number: searchItems.application_form_number,
            only_my_tasks: searchItems.only_my_tasks,
            work_start_start_date: searchItems.work_start_start_date,
            work_start_end_date: searchItems.work_start_end_date,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.ApplicationForm.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CSV_OUTPUT_FAILURE, params);
        }
    },

    /**
     * 申請書詳細関連情報イベントログ取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} id 申請書ID
     * @returns {Promise<void>}
     */
    async eventLogs({ dispatch, commit }, { pageNumber, perPage, sort, id } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
            id,
        };
        try {
            commit(types.EVENT_LOG_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.EVENT_LOG.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.EVENT_LOG_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.EVENT_LOG_FAILURE, params);
        }
    },

    /**
     * 申請書詳細関連情報操作記録取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 申請書ID
     * @returns {Promise<void>}
     */
    async workLogs({ dispatch, commit }, id: number) {
        try {
            commit(types.WORK_LOG_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.WORK_LOG.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.WORK_LOG_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.WORK_LOG_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 申請書詳細添付ファイルアップロード（複数）
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 申請書ID
     * @param {Array<File>} file 添付ファイルの配列
     * @returns {Promise<void>}
     */
    async uploadFiles(
        { dispatch, commit, rootState },
        { id, type, files }: { id?: number; type?: string; files: Array<File> } = { files: [] }
    ) {
        try {
            commit(types.UPLOAD_FILES_START);
            let resData;
            for (const file of files) {
                const updated_at = ((file as any).lastModifiedDate as Date) ?? new Date(file.lastModified);
                resData = await dispatch(
                    'auth/api',
                    {
                        method: 'post',
                        url: ApiUrl.ApplicationForm.UPLOAD_FILE.replace(':id', id.toString()),
                        params: file,
                        headers: {
                            'X-ARIES-UPLOAD-FILE-NAME': encodeURIComponent(file.name),
                            'X-ARIES-UPLOAD-FILE-TYPE': type,
                            'X-ARIES-UPLOAD-FILE-UPDATED-AT': dayjs(updated_at).format('YYYY-MM-DDTHH:mm:ssZ'),
                        },
                        option: {
                            return_before_send: true,
                        },
                    },
                    { root: true }
                );
                if (isHappenedOtherPartyError(resData)) {
                    // システムエラー・予期せぬエラー時
                    commit(types.FATAL_FAILURE);
                    break;
                }
            }
            if (!isHappenedOtherPartyError(resData)) {
                // 成功時
                commit(types.UPLOAD_FILES_SUCCESS, resData);
            }
        } catch (resData) {
            const i18n = (rootState as any).i18n;

            // アプリケーションエラー時
            const params =
                resData == ErrorType.BeforeSendError // 添付ファイルが読み込めない場合 送信前に失敗する
                    ? {
                          errorTitle: translator(i18n, 'Attachment_Files'),
                          errorDetails: [
                              {
                                  code: AppContext.Error.FileReadFailure.CODE,
                                  message: translator(
                                      i18n,
                                      `error_message.${AppContext.Error.FileReadFailure.MESSAGE_KEY}`
                                  ),
                              },
                          ],
                      }
                    : {
                          errorTitle: resData.title,
                          errorDetails: resData.errors,
                      };
            commit(types.UPLOAD_FILES_FAILURE, params);
        }
    },

    /**
     * 申請書詳細添付ファイルアップロード
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 申請書ID
     * @param {File} file 添付ファイル
     * @returns {Promise<void>}
     */
    async uploadFile({ dispatch, commit, rootState }, { id, type, file }: { id?: number; type?: string; file: File }) {
        try {
            commit(types.UPLOAD_FILE_START);
            let resData;
            const updated_at = ((file as any).lastModifiedDate as Date) ?? new Date(file.lastModified);
            resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.ApplicationForm.UPLOAD_FILE.replace(':id', id.toString()),
                    params: file,
                    headers: {
                        'X-ARIES-UPLOAD-FILE-NAME': encodeURIComponent(file.name),
                        'X-ARIES-UPLOAD-FILE-TYPE': type,
                        'X-ARIES-UPLOAD-FILE-UPDATED-AT': dayjs(updated_at).format('YYYY-MM-DDTHH:mm:ssZ'),
                    },
                    option: {
                        return_before_send: true,
                    },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPLOAD_FILE_SUCCESS, resData);
            }
        } catch (resData) {
            const i18n = (rootState as any).i18n;

            // アプリケーションエラー時
            const params =
                resData == ErrorType.BeforeSendError // 添付ファイルが読み込めない場合 送信前に失敗する
                    ? {
                          errorTitle: translator(i18n, 'Attachment_Files'),
                          errorDetails: [
                              {
                                  code: AppContext.Error.FileReadFailure.CODE,
                                  message: translator(
                                      i18n,
                                      `error_message.${AppContext.Error.FileReadFailure.MESSAGE_KEY}`
                                  ),
                              },
                          ],
                      }
                    : {
                          errorTitle: resData.title,
                          errorDetails: resData.errors,
                      };
            commit(types.UPLOAD_FILE_FAILURE, params);
        }
    },

    /**
     * 申請書詳細添付ファイル削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {Array<number>} ids ファイルIDの配列
     * @returns {Promise<void>}
     */
    async destroyFiles({ dispatch, commit, rootState }, { ids } = {}) {
        const payload = {
            ids: ids,
        };
        try {
            commit(types.DESTROY_FILE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.ApplicationForm.DESTROY_FILE,
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_FILE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.ApplicationForm.FILE_DESTROY);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FILE_FAILURE, params);
        }
    },

    /**
     * 作業アカウントパスワード払い出し
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {String} applicationFormId 申請書ID
     * @param {String} id 申請書紐付き作業アカウントID
     * @returns {Promise<void>}
     */
    async passwordNotification(
        { dispatch, commit, rootState },
        { applicationFormId, id }: { applicationFormId: string; id: string }
    ) {
        try {
            commit(types.NOTIFICATION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.ApplicationForm.PASSWORD_NOTIFICATION.replace(
                        ':application_form_id',
                        applicationFormId
                    ).replace(':id', id),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.NOTIFICATION_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.NOTIFICATION_FAILURE, params);
        }
    },

    /**
     * ステータス毎の申請書件数の取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async numberPerStatus({ dispatch, commit, rootState }) {
        try {
            commit(types.NUMBER_PER_STATUS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.NUMBER_PER_STATUS,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.NUMBER_PER_STATUS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.NUMBER_PER_STATUS_FAILURE, params);
        }
    },

    /**
     * 申請書に対する可能な操作の取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async usableActions({ dispatch, commit }, id: number) {
        try {
            commit(types.USABLE_ACTIONS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.ApplicationForm.USABLE_ACTIONS.replace(':id', id.toString()),
                },
                { root: true }
            );

            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.USABLE_ACTIONS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.USABLE_ACTIONS_FAILURE, params);
        }
    },

    /**
     * 添付ファイルダウンロード処理開始
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async attachmentFileDownloadStart({ commit }) {
        commit(types.ATTACHMENT_FILE_DOWNLOAD_START);
    },

    /**
     * 添付ファイルダウンロード処理正常終了
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async attachmentFileDownloadSuccess({ commit }) {
        commit(types.ATTACHMENT_FILE_DOWNLOAD_SUCCESS);
    },

    /**
     * 添付ファイルダウンロード処理エラー終了
     * @param {any} commit Vuex変数
     * @param resData エラーレスポンス
     * @returns {Promise<void>}
     */
    async attachmentFileDownloadFailure({ commit }, resData) {
        const params = {
            errorTitle: resData.title,
            errorDetails: resData.errors,
        };
        commit(types.ATTACHMENT_FILE_DOWNLOAD_FAILURE, params);
    },

    /**
     * 申請書IDフィールドのクリア
     * @returns {Promise<void>}
     */
    async clearCreatedFormId({ commit }) {
        commit(types.CLEAR_CREATED_FORM_ID);
    },

    /**
     * 申請書作成後の状態クリーンアップ
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearCreateAfterSuccess({ commit }) {
        commit(types.CLEAR_CREATED_AFTER_SUCCESS);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
